// PersonalChat.tsx – FINAL PERFECT VERSION (Smart scroll + Edit/Delete working)

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Send, Edit2, Trash2, Check, X } from 'lucide-react';
import { AesEncrypt, AesDecrypt } from '../../../services/crypto';
import WebService from '../../../services/webservice';
import Swal from 'sweetalert2';
import './PersonalChats.css';

interface Message {
  _id?: string;
  sender: 'user' | 'admin';
  text: string;
  time: string;
  edited?: boolean;
}

const PersonalChat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // Two refs: one for container (to detect scroll position), one for bottom anchor
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const ticketData = (location.state as any)?.state || {};
  const ticket = {
    ticketId: id,
    title: ticketData.title || 'Support',
    userName: ticketData.userName || 'User',
    via: ticketData.via || 'Website',
    status: ticketData.status || 'Open',
    isUrgent: ticketData.isUrgent || false,
  };

  const fetchMessages = async () => {
    if (!id) return;
    try {
      const enc = AesEncrypt({ ticketId: id });
      const { data } = await axios.post(WebService + "api/get-support-messages", { Enc: enc });

      if (data.response === "S") {
        const decrypted = AesDecrypt(data.data);
        const msgs: Message[] = decrypted.messages || [];

        setMessages(msgs.map((msg, index) => ({
          ...msg,
          _id: msg._id && typeof msg._id === 'string' && msg._id.length > 10
            ? msg._id
            : `msg-${index}-${Date.now()}`
        })));
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  };

  // Smart auto-scroll: Only scroll to bottom if user is already near the bottom
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !messagesEndRef.current) return;

    const isNearBottom =
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight
      ) < 200; // 200px tolerance

    if (isNearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    // If user scrolled up → do nothing (let them read old messages)
  }, [messages]);

  useEffect(() => {
    if (id) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 2000);
      return () => clearInterval(interval);
    }
  }, [id]);

  const sendMessage = async () => {
    if (!message.trim() || !id) return;
    try {
      await axios.post(WebService + "api/send-support-message", {
        ticketId: id,
        text: message.trim()
      });
      setMessage('');
      fetchMessages();
    } catch (err) {
      console.error("Send failed:", err);
    }
  };

  const deleteMessage = async (msgId: string) => {
    if (!msgId || msgId.includes('msg-')) {
      Swal.fire('Error', 'Cannot delete this message', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete message?',
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.post(WebService + "api/delete-support-message", { messageId: msgId });
      if (res.data.response === "S") {
        fetchMessages();
        Swal.fire('Deleted!', 'Message removed.', 'success');
      } else {
        Swal.fire('Error', res.data.message || 'Delete failed', 'error');
      }
    } catch (err: any) {
      Swal.fire('Failed', err.response?.data?.message || 'Network error', 'error');
    }
  };

  const startEdit = (msg: Message) => {
    if (!msg._id || msg._id.includes('msg-')) {
      Swal.fire('Cannot edit', 'This message cannot be edited', 'info');
      return;
    }
    setEditingId(msg._id);
    setEditText(msg.text);
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) {
      cancelEdit();
      return;
    }

    try {
      const res = await axios.post(WebService + "api/update-support-message", {
        messageId: editingId,
        newText: editText.trim()
      });

      if (res.data.response === "S") {
        setEditingId(null);
        setEditText('');
        fetchMessages();
      } else {
        Swal.fire('Error', res.data.message || 'Update failed', 'error');
      }
    } catch (err: any) {
      Swal.fire('Failed', err.response?.data?.message || 'Network error', 'error');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

return (
  <div className="chat-layout">
    {/* Fixed Header */}
    <div className="chat-header">
      <button onClick={() => navigate(-1)} className="back-btn">
        <ChevronLeft size={24} />
      </button>
      <div className="header-info">
        <h2>{ticket.title}</h2>
        <p>Ticket #{ticket.ticketId} • {ticket.via}</p>
      </div>
      <div className={`status-badge ${ticket.status.toLowerCase()}`}>
        {ticket.status}
      </div>
    </div>

    {/* Scrollable Messages Area */}
    <div className="messages-container" ref={containerRef}>
      {messages.map((msg, index) => {
        const isAdmin = msg.sender === 'admin';
        const isEditing = editingId === msg._id;

        return (
          <div
            key={msg._id || `fallback-${index}`}
            className={`message-wrapper ${isAdmin ? 'admin-message' : 'user-message'}`}
          >
            <div className="avatar">
              {isAdmin ? 'A' : ticket.userName[0].toUpperCase()}
            </div>

            <div className="message-content">
              {isEditing ? (
                <div className="edit-mode">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoFocus
                    rows={3}
                  />
                  <div className="edit-actions">
                    <button onClick={saveEdit}><Check size={18} /></button>
                    <button onClick={cancelEdit}><X size={18} /></button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="message-bubble">
                    <p>{msg.text}</p>
                    <span className="timestamp">
                      {msg.time} {msg.edited && <small>(edited)</small>}
                    </span>
                  </div>

                  {isAdmin && (
                    <div className="message-actions">
                      <button onClick={() => startEdit(msg)} className="action-btn edit-btn">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteMessage(msg._id || '')} className="action-btn delete-btn">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>

    {/* Fixed Input Area */}
    <div className="chat-input-area">
      <input
        type="text"
        placeholder="Type your reply..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
          }
        }}
      />
      <button onClick={sendMessage} className="send-btn" disabled={!message.trim()}>
        <Send size={20} />
      </button>
    </div>
  </div>
);

};

export default PersonalChat;